import { useCallback, useMemo, useState } from 'react';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { useDatasets } from 'src/components/star-grid/datasets/hooks/useDatasets';
import { PNL_SOURCE_ID } from 'src/components/star-grid/datasets/datasetConstants';
import type { Dataset } from 'src/components/star-grid/datasets/datasetTypes';

export interface DataSourceSelection {
  readonly datasets: Dataset[];
  readonly datasetsLoading: boolean;
  readonly datasetsError: string | null;
  readonly selectedId: string;
  readonly selectedDatasetId: string | null;
  readonly selectPnl: () => void;
  readonly selectDataset: (id: string) => void;
  readonly canEditSelected: boolean;
  readonly wizardOpen: boolean;
  readonly wizardEditingId: string | null;
  readonly openEditWizard: () => void;
  readonly closeWizard: () => void;
  readonly onWizardSaved: (dataset: Dataset) => void;
  /** Counts wizard saves; a change means the datasource definitions may have changed. */
  readonly savedRevision: number;
}

type WizardMode = { readonly kind: 'closed' } | { readonly kind: 'edit'; readonly id: string };

/** Owns the Studio data source choice (P&L vs a dataset) and the edit wizard's open state; onboarding is under Admin. */
export function useDataSourceSelection(): DataSourceSelection {
  const auth = useAuth();
  const { datasets, loading, error, reload } = useDatasets(true);
  const [wizard, setWizard] = useState<WizardMode>({ kind: 'closed' });
  const [selectedId, setSelectedId] = useState<string>(PNL_SOURCE_ID);
  // Bumped on every wizard save, so the Studio can re-read a datasource it already has selected.
  const [savedRevision, setSavedRevision] = useState(0);

  const selectPnl = useCallback(() => setSelectedId(PNL_SOURCE_ID), []);
  const selectDataset = useCallback((id: string) => setSelectedId(id), []);

  const selectedDatasetId = selectedId === PNL_SOURCE_ID ? null : selectedId;
  const selectedDataset = useMemo(() => datasets.find((dataset) => dataset.id === selectedDatasetId), [datasets, selectedDatasetId]);
  const canEditSelected = Boolean(selectedDataset && auth.Alias && (selectedDataset.owner ?? selectedDataset.created_by) === auth.Alias);

  const openEditWizard = useCallback(() => {
    if (selectedDatasetId) setWizard({ kind: 'edit', id: selectedDatasetId });
  }, [selectedDatasetId]);
  const closeWizard = useCallback(() => setWizard({ kind: 'closed' }), []);

  // A draft cannot be reported on yet, so only a published datasource becomes the selected source.
  const onWizardSaved = useCallback(
    (dataset: Dataset) => {
      reload();
      setSavedRevision((revision) => revision + 1);
      if (dataset.status === 'published') setSelectedId(dataset.id);
    },
    [reload]
  );

  return {
    datasets,
    datasetsLoading: loading,
    datasetsError: error,
    selectedId,
    selectedDatasetId,
    selectPnl,
    selectDataset,
    canEditSelected,
    wizardOpen: wizard.kind !== 'closed',
    wizardEditingId: wizard.kind === 'edit' ? wizard.id : null,
    openEditWizard,
    closeWizard,
    onWizardSaved,
    savedRevision
  };
}
